// Draws one crosslink relation into the CrosslinkHoverLayer source: both members'
// sections, a ribbon between them (red when out of bounds) and endpoint markers.
// Used by the map hover (CrosslinkLayerHandler) and the sidebar member hover.
import Feature from "ol/Feature";
import Point from "ol/geom/Point";
import Polygon from "ol/geom/Polygon";
import { sliceOlLineByCm } from "../../util";

// Endpoint-marker colours. Corresponding ends of the matched road and lane
// sections share a colour, so it's visible which end maps to which.
export const CROSSLINK_MATCH_START_COLOR = "#ffd400"; // lower-cm end of the match
export const CROSSLINK_MATCH_END_COLOR = "#7c4dff"; // higher-cm end of the match

// Unique suffix per drawn feature so the viz source never sees duplicate ids
// (OpenLayers throws on those) while keeping the original id prefix —
// CrosslinkHoverLayer's isSD() only inspects the leading "6:".
let sectionIndex = 0;

function addSections(vizSource, srcFeature, ranges) {
  const geometries =
    ranges.length === 0
      ? [srcFeature.getGeometry().clone()]
      : ranges.map((range) =>
          sliceOlLineByCm(srcFeature.getGeometry(), range.startCm, range.endCm),
        );
  geometries.forEach((geometry) => {
    const sectionFeature = new Feature({ geometry });
    sectionFeature.setId(`${srcFeature.getId()}#${sectionIndex++}`);
    vizSource.addFeature(sectionFeature);
  });
  return geometries;
}

function addMarker(vizSource, coordinate, color) {
  const marker = new Feature({ geometry: new Point(coordinate) });
  marker.set("markerColor", color);
  marker.setId(`crosslink-marker#${sectionIndex++}`);
  vizSource.addFeature(marker);
}

// Mark the matching ends of the road and lane sections. `opposite` (from the
// relation's direction tag) decides which end pairs with which.
function addEndpointMarkers(vizSource, geomA, geomB, opposite) {
  addMarker(vizSource, firstCoord(geomA), CROSSLINK_MATCH_START_COLOR);
  addMarker(
    vizSource,
    opposite ? lastCoord(geomB) : firstCoord(geomB),
    CROSSLINK_MATCH_START_COLOR,
  );
  addMarker(vizSource, lastCoord(geomA), CROSSLINK_MATCH_END_COLOR);
  addMarker(
    vizSource,
    opposite ? firstCoord(geomB) : lastCoord(geomB),
    CROSSLINK_MATCH_END_COLOR,
  );
}

// Semi-transparent ribbon spanning the gap between the matched road and lane
// sections — a visual cue of what maps to what. The ring walks A start→end, then
// B back toward A's start: for `same` direction that means B reversed
// (end→start); for `opposite`, B as-is.
function addConnectingPolygon(vizSource, geomA, geomB, opposite, outOfBounds) {
  const a = geomA.getCoordinates();
  const b = geomB.getCoordinates();
  const ring = opposite ? [...a, ...b] : [...a, ...b.slice().reverse()];
  ring.push(ring[0]);
  const polygon = new Feature({ geometry: new Polygon([ring]) });
  polygon.setId(`crosslink-polygon#${sectionIndex++}`);
  // Flags an out-of-bounds relation so the hover layer paints the ribbon red
  // instead of blue (CrosslinkHoverLayer reads this).
  polygon.set("outOfBounds", outOfBounds === true);
  vizSource.addFeature(polygon);
}

/**
 * @param {import("ol/source/Vector").default} vizSource
 * @param {{relation: Feature, featureA: Feature, featureB: Feature,
 *   rangesA: {startCm: number, endCm: number}[], rangesB: {startCm: number, endCm: number}[],
 *   outOfBounds: boolean}} drawing `[]` ranges draw the whole member
 */
export function drawCrosslinkRelation(
  vizSource,
  { relation, featureA, featureB, rangesA, rangesB, outOfBounds },
) {
  const geomA = addSections(vizSource, featureA, rangesA)[0];
  const geomB = addSections(vizSource, featureB, rangesB)[0];
  if (geomA === undefined || geomB === undefined) return;
  const opposite = isOpposite(
    getDirectionTag(relation),
    firstCoord(geomA),
    lastCoord(geomA),
    firstCoord(geomB),
    lastCoord(geomB),
  );
  addConnectingPolygon(vizSource, geomA, geomB, opposite, outOfBounds);
  addEndpointMarkers(vizSource, geomA, geomB, opposite);
}

function firstCoord(geometry) {
  return geometry.getCoordinates()[0];
}

function lastCoord(geometry) {
  const coordinates = geometry.getCoordinates();
  return coordinates[coordinates.length - 1];
}

// The crosslink relation's mandatory `direction` tag (same|opposite). Falls
// back to a prefixed/namespaced key (e.g. "<layerId>-direction", ":direction")
// in case the WFS export renames it.
function getDirectionTag(relation) {
  const direct = relation.get("direction");
  if (direct !== undefined) {
    return direct;
  }
  const key = relation
    .getKeys()
    .find((candidate) => /(^|[-:])direction$/.test(candidate));
  return key ? relation.get(key) : undefined;
}

// Whether the two sections run in opposite directions. Prefer the crosslink
// `direction` tag; if it's missing/unknown, infer from geometry by choosing the
// endpoint pairing with the shorter total distance.
function isOpposite(direction, aStart, aEnd, bStart, bEnd) {
  const normalized =
    typeof direction === "string" ? direction.toLowerCase() : direction;
  if (normalized === "same") {
    return false;
  }
  if (normalized === "opposite") {
    return true;
  }
  const sameCost = distance(aStart, bStart) + distance(aEnd, bEnd);
  const oppositeCost = distance(aStart, bEnd) + distance(aEnd, bStart);
  return oppositeCost < sameCost;
}

function distance(a, b) {
  return Math.hypot(a[0] - b[0], a[1] - b[1]);
}
